import { type FormFieldNestedListOptions, type FormFieldNestedListValues, IkeToken } from '@ikegps/core-api';
import { FormModelFieldNestedList, FormModelFieldNestedListBuilder } from '@ikegps/core-shared';
import { action, observable, makeObservable } from 'mobx';
import { ImageButton } from '../../../components/button/image.button';
import { MaterialIcon } from '../../../components/icon/icons';
import { Input } from '../../../components/input/input';
import { InputState, InputStateTypes } from '../../../components/input/input.state';
import { IkeInputStateGroup } from '../../../components/input/input.state.group';
import { ModalService } from '../../../components/modal/modal.service';
import { addStyles, DefaultTheme } from '../../../components/theme';
import { IkeCssUtil } from '../../../components/theme.util';
import { T, Translate } from '../../../i18n';
import type { MithrilVNode } from '../../../mithril';
import { m } from '../../../mithril';
import { Services } from '../../../services';
import { FormDesigner } from '../form.designer';
import { FormFieldEditor } from './form.field.editor';

export const CSS = {
	container: 'c-FdcNestedList',
	list: 'c-FdcNestedList__list',
	option: 'c-FdcNestedList__option',
	value: 'c-FdcNestedList__value',
	title: 'c-FdcNestedList__title',
	color: 'c-FdcNestedList__color',
	colorTitle: 'c-FdcNestedList__colorTitle',

	isReadOnly: 'FdcNestedList--is-read-only',
	isReadWrite: 'FdcNestedList--is-read-write',
	isSelected: 'FdcNestedList--is-selected',
	isSelectable: 'FdcNestedList--is-selectable',
};

const colorPickerSize = 32;
const colorPickerMargin = 20;
const inputWidth = 248;

addStyles({
	[CSS.container]: {
		display: 'flex',
	},
	[CSS.list]: {
		...IkeCssUtil.Flex.column,
		width: '100%',
		'max-width': '340px',
	},
	[CSS.title]: {
		display: 'flex',
		alignItems: 'center',
		height: '20px',
		'text-align': 'left',
		'margin-bottom': DefaultTheme.space(4),
		'& .mdl-textfield': {
			width: `${inputWidth}px`,
		},
		'& input': {
			'font-weight': DefaultTheme.font.weight.bold,
		},
	},
	[CSS.value]: {
		...IkeCssUtil.Flex.centerVertical,
		'text-align': 'left',
		'text-transform': 'none',
		'min-height': '36px',
		'& input': {
			outline: 'none',
		},
		'& button': {
			width: '300px',
			'white-space': 'normal',
			'word-break': 'break-word',
			height: 'auto',
			'min-height': '36px',
			'line-height': 'normal',
			overflow: 'visible',
		},
		'& .mdl-textfield': {
			width: `${inputWidth}px`,
		},
	},
	[CSS.isSelected]: {
		color: DefaultTheme.font.color.link,
	},
	[CSS.isSelectable]: {
		cursor: 'pointer',
	},

	[CSS.colorTitle]: {
		width: `${colorPickerSize}px`,
		marginRight: `${colorPickerMargin}px`,

		'font-weight': 'bold',
		color: DefaultTheme.font.color.supporting,

		[`& + .mdl-textfield`]: {
			width: `${300 - colorPickerSize - colorPickerMargin}px`,
		},
	},
	[CSS.color]: {
		width: `${colorPickerSize}px`,
		height: '20px',
		overflow: 'hidden',
		boxSizing: 'border-box',

		borderRadius: '4px',

		border: '2px solid #c9c9c9',

		marginRight: `${colorPickerMargin}px`,

		['& input[type=color]']: {
			width: 'calc(100% + 12px)',
			height: 'calc(100% + 12px)',

			padding: 0,
			margin: '-6px',

			cursor: 'pointer',
		},

		[`& + .${CSS.value} > .mdl-textfield`]: {
			width: `calc(300px - ${colorPickerSize + colorPickerMargin}px)`,
		},
	},
});

type NestedListNodeMaker = (
	child: FormFieldNestedListValues,
	index: number,
	offset: number,
	isSelected: boolean
) => MithrilVNode;

/** Maximum number of levels to support. Too many becomes difficult to edit and visualize. */
const MAX_OPTIONS = 3;

export class FormFieldEditorNestedList extends FormFieldEditor<FormModelFieldNestedList> {
	selected = observable.array<number>();
	stateGroups: IkeInputStateGroup[] = [];
	builder: FormModelFieldNestedListBuilder;
	isAnalyst = Services.User.isIkeAnalyst(Services.User.current);

	constructor(designer: FormDesigner, field: FormModelFieldNestedList) {
		super(designer, field);

		makeObservable(this);

		// Init the selected to the first option every level
		this.builder = field.Mutator.builder;
		const options = this.builder.options;
		if (options != null && Array.isArray(options.values) && options.values.length > 0) {
			this.setSelected(0, 0);
		}

		if (options == null) {
			this.builder.createGroup(this.getOptionTitle(0));
		}
	}

	override viewInfoRW() {
		return m('div', { className: CSS.container }, this.viewNestedOptionsRW(this.field.options));
	}

	override viewInfoRO() {
		return m('div', { className: CSS.container }, this.viewNestedOptionsRO(this.field.options));
	}

	private getCurrentOption(path: number[]): FormFieldNestedListOptions {
		let opts = this.field.options;

		for (const pathIndex of path) {
			if (opts == null || opts.values == null || pathIndex >= opts.values.length) {
				return null;
			}
			opts = opts.values[pathIndex].options;
		}

		if (opts == null || opts.values == null) {
			return null;
		}

		return opts;
	}

	private getCurrentValue(path: number[], offset: number): FormFieldNestedListValues {
		const opts = this.getCurrentOption(path);
		if (opts == null) {
			return null;
		}
		return opts.values[offset];
	}

	private getCurrentValueString(path: number[], offset: number, type?: string): string {
		const node = this.getCurrentValue(path, offset);
		if (node == null) {
			return '';
		}
		if (type === InputStateTypes.COLOR) {
			return node.color;
		}
		return node.value;
	}

	private destroyStateGroup(index: number) {
		// If no change is required, avoid touching the array and causing a reaction.
		if (index >= this.stateGroups.length) {
			return;
		}

		for (let i = index; i < this.stateGroups.length; i++) {
			this.stateGroups[i].destroy();
		}

		this.stateGroups = this.stateGroups.slice(0, index);
	}

	private getStateGroup(index: number) {
		let existing = this.stateGroups[index];
		if (existing == null) {
			existing = this.stateGroups[index] = new IkeInputStateGroup();
			this.state.add(existing);
		}
		return existing;
	}

	private getColorState(index: number, offset: number): InputState<string> {
		const path = this.selected.slice(0, index);
		const currentSelection = path.join('__');
		const fieldKey = `${currentSelection}:${offset}:color`;

		const state = this.getStateGroup(index);
		let editState = state.get(fieldKey);

		if (editState == null) {
			editState = new InputState(T.EMPTY, fieldKey, InputStateTypes.COLOR);

			state.add(editState);
			editState.onValue((value) => {
				const group = this.builder.getGroup(path);
				if (group == null) {
					const groupTitle = this.getOptionTitle(index);
					this.builder.getValue(path).createGroup(groupTitle).createValue(value);
				} else if (offset < group.size) {
					group.getValue(offset).setColor(value);
				} else {
					group.createValue(value);
				}

				this.applyChanges();
			});

			editState.setValue(this.getCurrentValueString(path, offset, InputStateTypes.COLOR) || '#a0aec0');
			editState.setPlaceholder(Translate(T.FIELD_NESTED_LIST_VALUE));
			editState.reaction(
				() => editState.focused,
				(focused) => focused && this.setSelected(index, offset)
			);
		}

		editState.isRequired = this.isExistingOption(path, offset);
		return editState;
	}

	private getValueState(index: number, offset: number): InputState<string> {
		const path = this.selected.slice(0, index);
		const currentSelection = path.join('__');
		const fieldKey = `${currentSelection}:${offset}`;

		const state = this.getStateGroup(index);
		let editState = state.get(fieldKey);

		if (editState == null) {
			editState = new InputState(T.EMPTY, fieldKey, InputStateTypes.TEXT);

			state.add(editState);
			editState.onValue((value) => {
				const group = this.builder.getGroup(path);
				if (group == null) {
					const groupTitle = this.getOptionTitle(index);
					this.builder.getValue(path).createGroup(groupTitle).createValue(value);
				} else if (offset < group.size) {
					group.getValue(offset).setValue(value);
				} else {
					group.createValue(value);
				}

				this.applyChanges();
			});

			editState.setValue(this.getCurrentValueString(path, offset));
			editState.setPlaceholder(Translate(T.FIELD_NESTED_LIST_VALUE));
			editState.reaction(
				() => editState.focused,
				(focused) => focused && this.setSelected(index, offset)
			);
		}

		editState.isRequired = this.isExistingOption(path, offset);
		return editState;
	}

	private isExistingOption(path: number[], offset: number): boolean {
		const opts = this.getCurrentOption(path);

		// This is a placeholder option for a new group and not required.
		if (opts == null) {
			return false;
		}

		// This is a placeholder option for an empty group and is required.
		if (opts.values.length === 0) {
			return true;
		}

		// This is a placeholder option for a non-empty group and is not required.
		if (offset >= opts.values.length) {
			return false;
		}

		// All other options are required.
		return true;
	}

	private getOptionTitleState(index: number): InputState<string> {
		const path = this.selected.slice(0, index);
		const currentSelection = path.join('__');
		const fieldKey = `${currentSelection}:title`;
		const state = this.getStateGroup(index);
		const level = index + 1;
		let editState = state.get(fieldKey);

		if (editState == null) {
			editState = new InputState(T.EMPTY, fieldKey, InputStateTypes.TEXT);
			state.add(editState);
			editState.isRequired = true;
			editState.onValue((value) => {
				const group = this.builder.getGroup(path);
				if (group != null) {
					group.setTitle(value);
				} else {
					this.builder.getValue(path).createGroup(value);
				}
				this.applyChanges();
			});

			const opt = this.getCurrentOption(path);
			if (opt != null && opt.title != null && opt.title.length > 0) {
				editState.setValue(opt.title);
			} else {
				editState.setValue(Translate(T.FIELD_NESTED_LIST_TITLE, { level }));
			}

			editState.setPlaceholder(Translate(T.FIELD_NESTED_LIST_TITLE, { level }));
		}

		return editState;
	}

	private getOptionTitle(index: number): string {
		return this.getOptionTitleState(index).value;
	}

	private async confirmDeleteValue(index: number, offset: number) {
		// Don't ask if there are no nested options
		const path = [...this.selected.slice(0, index), offset];
		if (this.builder.getGroup(path) == null) {
			this.deleteValue(index, offset);
			return;
		}

		const result = await ModalService.open({
			title: Translate(T.FORM_FIELD_NESTED_LIST_DELETE_TITLE),
			content: Translate(T.FORM_FIELD_NESTED_LIST_DELETE_CONFIRM),
			hasCancel: true,
			okName: T.FORM_FIELD_NESTED_LIST_DELETE,
		});

		if (result.ok) {
			this.deleteValue(index, offset);
		}
	}

	@action private deleteValue(index: number, offset: number) {
		const path = this.selected.slice(0, index);

		this.builder.getGroup(path).deleteValue(offset);
		if (this.builder.getGroup(path).size === 0) {
			const prevPath = path.slice(0, path.length - 1);
			if (prevPath.length > 0) {
				const newIndex = path[path.length - 1];
				const oldValue = this.builder.getGroup(prevPath).getValue(newIndex);
				this.builder.getGroup(prevPath).deleteValue(newIndex);
				this.builder.getGroup(prevPath).createValue(oldValue.value, null, oldValue.color, newIndex);
			}
		}
		this.applyChanges();

		this.destroyStateGroup(index);

		this.setSelected(index, Math.max(0, offset - 1));
	}

	private viewNestedOptionButton(value: string, isSelected: boolean, index: number, offset: number) {
		let className = `mdl-button ${CSS.value} ${CSS.isReadOnly}`;
		if (isSelected) {
			className += ' ' + CSS.isSelected;
		} else {
			className += ' ' + CSS.isSelectable;
		}
		return m(
			'button',
			{
				className,
				onclick: isSelected ? null : () => this.setSelected(index, offset),
			},
			value
		);
	}

	private viewNestedOptionsRW(opt: FormFieldNestedListOptions): MithrilVNode[] {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const editor = this;

		function makeNode(child: FormFieldNestedListValues, index: number, offset: number, isSelected: boolean) {
			let className = `${CSS.value}`;
			if (isSelected) {
				className += ' ' + CSS.isSelected;
			}

			const showColor = Services.Token.has(IkeToken.FEATURE_COLOR_FLAG) && editor.field.isMapColorField && index === 0;

			const children = [];

			if (editor.field.isLocked && !editor.isAnalyst) {
				children.push(editor.viewNestedOptionButton(child.value, isSelected, index, offset));
			} else {
				if (showColor) {
					children.push(
						m('div', { className: CSS.color }, Input.view(editor.getColorState(index, offset), { rawInputOnly: true }))
					);
				}

				children.push(m('div', { className }, Input.view(editor.getValueState(index, offset))));
			}

			if (isSelected) {
				if (!(editor.field.isLocked && !editor.isAnalyst))
					children.push(
						ImageButton.view(MaterialIcon.DELETE, {
							onclick: () => editor.confirmDeleteValue(index, offset),
						})
					);
			}

			return m('div', { className }, children);
		}
		return this.viewNestedOptions(opt, makeNode);
	}

	private viewNestedOptionsRO(opt: FormFieldNestedListOptions): MithrilVNode[] {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const editor = this;

		function makeNode(child: FormFieldNestedListValues, index: number, offset: number, isSelected: boolean) {
			return editor.viewNestedOptionButton(child.value, isSelected, index, offset);
		}
		return this.viewNestedOptions(opt, makeNode);
	}

	private viewNestedOptions(
		opt: FormFieldNestedListOptions,
		nodeMaker: NestedListNodeMaker,
		index = 0
	): MithrilVNode[] {
		if (opt == null) {
			if (index > this.selected.length || index >= MAX_OPTIONS) {
				return [];
			}

			opt = { title: '', values: [] };
		}

		const children: MithrilVNode[] = [this.viewOptionTitle(opt, index)];
		let levels: MithrilVNode[] = [];

		for (let offset = 0; offset < opt.values.length; offset++) {
			const child = opt.values[offset];
			const isSelected = this.selected.length > index && offset === this.selected[index];

			let node: MithrilVNode;
			if (isSelected) {
				levels = this.viewNestedOptions(child.options, nodeMaker, index + 1);
				node = nodeMaker(child, index, offset, isSelected);
			} else {
				node = nodeMaker(child, index, offset, isSelected);
			}

			children.push(node);
		}

		if (!(this.field.isLocked && !this.isAnalyst) && this.designer.isEditing) {
			children.push(nodeMaker({ value: '' }, index, opt.values.length, false));
		}

		return [m('div', { className: CSS.list }, children), ...levels];
	}

	private viewOptionTitle(opt: FormFieldNestedListOptions, index: number): MithrilVNode {
		const children = [];

		if (Services.Token.has(IkeToken.FEATURE_COLOR_FLAG) && this.field.isMapColorField && index === 0) {
			children.push(m('div', { className: CSS.colorTitle }, Translate(T.COLOR)));
		}

		if (!this.field.isLocked && this.designer.isEditing) {
			const inputState = this.getOptionTitleState(index);
			children.push(Input.view(inputState));

			return m('div', { className: CSS.title }, children);
		}

		children.push(opt.title);

		return m('div', { className: CSS.title }, children);
	}

	@action private setSelected(index: number, offset: number) {
		if (this.selected.length > index && this.selected[index] === offset) {
			return;
		}

		this.destroyStateGroup(index);

		if (this.selected.length > index) {
			this.selected.splice(index);
		}

		this.selected.push(offset);

		// Set the selected to the first value down the nest
		const nestedValue = this.getCurrentValue(this.selected.slice(), offset);

		if (nestedValue == null) {
			return;
		}

		let opt = nestedValue.options;
		while (opt != null && Array.isArray(opt.values)) {
			const current = opt.values[0];
			if (current == null) {
				break;
			}
			this.selected.push(0);
			opt = current.options;
		}
	}

	private applyChanges() {
		this.field.Mutator.init(this.builder.options);
	}
}
